/**
 * Platform admin Schools tab: every school with its admins and entry points
 * into school management and the per-school actions menu.
 */
import React from 'react';
import { View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../../../hooks/useTranslation';
import { ROUTES } from '../../../../navigation/routes';
import type { School, SchoolAdmin } from '../platformAdminTypes';

interface SchoolsTabProps {
  schools: School[];
  adminsBySchool: Record<number, SchoolAdmin[]>;
  onCreateSchool: () => void;
  onResendAdminInvite: (schoolId: number, adminId: number) => void;
  onAddAdmin: (schoolId: number) => void;
  onOpenSchoolSettings: (school: School) => void;
}

export default function SchoolsTab({
  schools,
  adminsBySchool,
  onCreateSchool,
  onResendAdminInvite,
  onAddAdmin,
  onOpenSchoolSettings,
}: SchoolsTabProps) {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <View className="gap-4">
      <View className="flex-row justify-between items-center">
        <Text className="text-xl font-bold text-foreground">
          {t('platformAdmin.schoolsList')} ({schools.length})
        </Text>
        <Button variant="default" size="sm" onPress={onCreateSchool}>
          <MaterialIcons name="add" size={16} color="white" />
          <Text className="text-white font-semibold ml-2">{t('platformAdmin.create')}</Text>
        </Button>
      </View>

      {schools.length > 0 ? (
        <View className="gap-4">
          {schools.map((school) => (
            <Card key={school.id}>
              <CardHeader className="pb-3">
                <View className="flex-row justify-between items-start">
                  <View className="flex-1">
                    <Text className="text-lg font-bold text-foreground mb-1">
                      {school.name}
                    </Text>
                    <Text className="text-sm font-semibold text-primary">
                      @{school.slug}
                    </Text>
                  </View>
                  <View
                    className={`px-3 py-1 rounded-full ${school.is_active ? 'bg-green-100' : 'bg-red-100'
                      }`}
                  >
                    <Text
                      className={`text-xs font-semibold ${school.is_active ? 'text-green-700' : 'text-red-700'
                        }`}
                    >
                      {school.is_active ? t('schoolManagement.active') : t('schoolManagement.inactive')}
                    </Text>
                  </View>
                </View>
              </CardHeader>

              <CardContent>
                <View className="gap-2 mb-4">
                  <View className="flex-row items-center">
                    <MaterialIcons name="group" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2">
                      {school.current_students + (school.current_mentors || 0)}/{school.max_students} {t('platformAdmin.billableSeats')}
                    </Text>
                  </View>

                  <View className="flex-row items-center">
                    <MaterialIcons name="payment" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2">
                      {school.subscription_type} • {school.subscription_status}
                    </Text>
                  </View>

                  <View className="flex-row items-center">
                    <MaterialIcons name="calendar-today" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2">
                      {t('platformAdmin.createdOn')} {new Date(school.created_at).toLocaleDateString()}
                    </Text>
                  </View>

                  {/* Admins Section */}
                  <View className="mt-2">
                    <View className="flex-row items-center mb-2">
                      <MaterialIcons name="supervisor-account" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2 font-medium">{t('platformAdmin.admins')}</Text>
                    </View>
                    {(adminsBySchool[school.id] || []).length === 0 ? (
                      <Text className="text-sm text-muted-foreground ml-6 italic">{t('platformAdmin.noAdmins')}</Text>
                    ) : (
                      <View className="gap-2 ml-6">
                        {(adminsBySchool[school.id] || []).map((admin) => (
                          <View key={admin.id} className="flex-row items-center justify-between">
                            <Text className="text-sm text-muted-foreground flex-1">
                              {admin.full_name || admin.email}
                              {admin.invitation_pending ? ` • ${t('platformAdmin.pendingInvitation')}` : ''}
                            </Text>
                            <Button variant="outline" size="sm" onPress={() => onResendAdminInvite(school.id, admin.id)}>
                              <Text className="text-xs">{t('platformAdmin.resend')}</Text>
                            </Button>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                </View>

                {/* Action Buttons */}
                <View className="flex-row flex-wrap gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onPress={() => onAddAdmin(school.id)}
                  >
                    <Text className="text-white text-xs">{t('platformAdmin.addAdmin')}</Text>
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onPress={() => router.push({
                      pathname: ROUTES.TABS_SCHOOL_MANAGEMENT,
                      params: { schoolId: String(school.id), tab: 'students' },
                    })}
                  >
                    <Text className="text-xs">{t('platformAdmin.viewStudents')}</Text>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => router.push({
                      pathname: ROUTES.TABS_SCHOOL_MANAGEMENT,
                      params: { schoolId: String(school.id), tab: 'mentors' },
                    })}
                  >
                    <MaterialIcons name="school" size={14} color="#1F2937" />
                    <Text className="text-xs ml-1">{t('platformAdmin.manageMentors')}</Text>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => onOpenSchoolSettings(school)}
                  >
                    <Text className="text-xs">{t('platformAdmin.schoolSettings')}</Text>
                  </Button>
                </View>
              </CardContent>
            </Card>
          ))}
        </View>
      ) : (
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="school" size={48} color="#6b7280" />
            <Text className="text-lg font-medium text-foreground mt-4 mb-2">
              {t('platformAdmin.noSchools')}
            </Text>
            <Text className="text-base text-muted-foreground text-center mb-6">
              {t('platformAdmin.createFirstSchool')}
            </Text>
            <Button variant="default" onPress={onCreateSchool}>
              <Text className="text-white font-medium">{t('platformAdmin.createSchool')}</Text>
            </Button>
          </CardContent>
        </Card>
      )}
    </View>
  );
}
